import { alignCenterCellStyle, alignLeftCellStyle } from '@client-shared/ui-agGrid';
import { adminColumnSharedFields, periodColumnDefinition } from '../../shared/columnDefinitions';
import ScenariosAdminColumnRenderer from './adminColumnRenderer';
import { dataSourcesOptions, yesNoFlagOptions } from './constants';
import { GridMode } from '../../shared/gridEdit/useEditableGridLogic';
import SelectCellEditor, { baseCellEditorParams } from '../../shared/gridEdit/selectCellEditor.component';
import appendErrorIndicatorRenderer from '../../shared/gridEdit/validationMessagesIndicatorCellRenderer.component';
import ScenarioLinkCellRenderer from './scenarioLinkCellRenderer.component';
import DateCellRenderer from '../../shared/agGrid/cellRenderers/dateCellRenderer.component';
import { getScenarioStatus } from './helpers';

const editableFactory = (allowedModes, additionalCondition = () => true) =>
  ({ data, context }) => {
    if (!context || !data) {
      return () => false;
    }
    return allowedModes.some(allowed => allowed === context.mode)
    && (
      (context.mode === GridMode.Add && data.meta.isAdded)
      || (context.mode === GridMode.Edit && context.editedRowId === data.scenarioId)
    )
    && additionalCondition({ data, context });
  };

const getEditableColumnDefinition = ({ setRowData, editable = () => true }) => ({
  editable,
  cellClassRules: {
    'disable-editing': args => !editable(args),
  },
  valueSetter: params => setRowData(params),
  filterParams: { newRowsAction: 'keep' },
});

const getAdminColumnValue = ({ data }) => {
  if (!data) {
    return '';
  }
  if (data.meta.isUpdated) {
    return 'Update';
  }
  if (data.meta.isAdded) {
    return 'Add';
  }
  return '';
};

const getAdminColumnHeaderValue = ({ context }) =>
  context.mode === GridMode.Edit || context.mode === GridMode.EditAll || context.mode === GridMode.Add ? 'Add / Update' : '';

const isNotCopyScenario = ({ data }) => !data.sourceName;

const getColumnDefinitions = params => [
  {
    ...adminColumnSharedFields,
    field: 'scenarioId',
    colId: 'scenarioId',
    cellRenderer: ScenariosAdminColumnRenderer,
    cellRendererParams: () => ({
      editRow: params.editRow,
      context: params.context,
      cloneScenario: params.cloneScenario,
    }),
    cellClassRules: {
      'disable-editing': () => true,
    },
    valueGetter: getAdminColumnValue,
    headerValueGetter: () => getAdminColumnHeaderValue({ context: params.context }),
  },
  ...appendErrorIndicatorRenderer([
    {
      headerName: 'Scenario Name',
      field: 'scenarioName',
      colId: 'scenarioName',
      cellRenderer: ScenarioLinkCellRenderer,
      cellRendererParams: () => ({
        showLink: params.context.mode === GridMode.View,
      }),
      ...alignLeftCellStyle,
      ...getEditableColumnDefinition({
        ...params,
        editable: editableFactory([GridMode.Add, GridMode.Edit]),
      }),
      valueParser: ({ newValue, oldValue }) => newValue !== undefined ? newValue : oldValue, // eslint-disable-line no-undefined
    }]),
  ...appendErrorIndicatorRenderer([
    {
      ...periodColumnDefinition,
      colId: 'periodName',
      ...getEditableColumnDefinition({
        ...params,
        editable: editableFactory([GridMode.Add], isNotCopyScenario),
      }),
      cellEditor: SelectCellEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        options: params.periods,
        ...baseCellEditorParams,
      },
    },
    {
      headerName: 'Data Source',
      field: 'dataSource',
      colId: 'dataSource',
      ...alignCenterCellStyle,
      ...getEditableColumnDefinition({
        ...params,
        editable: editableFactory([GridMode.Add], isNotCopyScenario),
      }),
      cellEditor: SelectCellEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        options: dataSourcesOptions,
        stopEditing: params.stopEditing,
        ...baseCellEditorParams,
      },
    },
    {
      headerName: 'Source name',
      field: 'sourceName',
      colId: 'sourceName',
      ...alignLeftCellStyle,
      ...getEditableColumnDefinition({
        ...params,
        editable: () => false,
      }),
    },
    {
      headerName: 'Public',
      field: 'publicFlag',
      colId: 'publicFlag',
      width: 80,
      ...getEditableColumnDefinition({
        ...params,
        editable: editableFactory(
          [GridMode.Add, GridMode.Edit],
          () => params.canCreatePublicScenario
        ),
      }),
      cellEditor: SelectCellEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        options: yesNoFlagOptions,
        ...baseCellEditorParams,
      },
    },
    {
      headerName: 'Locked',
      field: 'lockedFlag',
      colId: 'lockedFlag',
      width: 80,
      ...getEditableColumnDefinition({
        ...params,
        editable: editableFactory([GridMode.Add, GridMode.Edit]),
      }),
      cellEditor: SelectCellEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        options: yesNoFlagOptions,
        ...baseCellEditorParams,
      },
    },
    {
      headerName: 'Status',
      field: 'modifiedFlag',
      colId: 'modifiedFlag',
      width: 80,
      ...getEditableColumnDefinition({
        ...params,
        editable: () => false,
      }),
      valueGetter: ({ data }) => getScenarioStatus({ modifiedFlag: data.modifiedFlag, lockedFlag: data.lockedFlag }),
    },
  ]),
  {
    headerName: 'Last update',
    field: 'updatedOn',
    colId: 'updatedOn',
    cellRenderer: DateCellRenderer,
    width: 140,
    minWidth: 100,
    ...alignLeftCellStyle,
    cellClassRules: {
      'disable-editing': () => true,
    },
  },
  {
    headerName: 'Updated by',
    field: 'updatedBy',
    colId: 'updatedBy',
    width: 140,
    minWidth: 100,
    ...alignLeftCellStyle,
    cellClassRules: {
      'disable-editing': () => true,
    },
  },
];


export default getColumnDefinitions;
